import { monthLabel } from "@/lib/format";
import { centsEur, type Stage } from "@/lib/documents";
import type { Pipeline as PipelineData } from "@/lib/types";
import { cn } from "@/lib/utils";

const short = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;

function stages(p: PipelineData | undefined): { id: Stage; label: string; dot: string; amount: string; sub: string }[] {
  const n = (c: number, one: string, many: string) => `${c} ${c === 1 ? one : many}`;
  return [
    {
      id: "ready", label: "Zu verrechnen", dot: "bg-muted-foreground",
      amount: p ? centsEur(p.ready.cents) : "–",
      sub: !p ? " " : p.ready.oldest ? `${n(p.ready.customers, "Kunde", "Kunden")} · ältester Posten seit ${monthLabel(p.ready.oldest.slice(0, 7)).split(" ")[0]}` : "nichts offen",
    },
    {
      id: "draft", label: "Entwürfe", dot: "bg-border ring-1 ring-muted-foreground ring-inset",
      amount: p ? centsEur(p.drafts.cents) : "–", sub: p ? `${p.drafts.count} noch nicht versendet` : " ",
    },
    {
      id: "open", label: "Offen", dot: "bg-warning",
      amount: p ? centsEur(p.open.cents) : "–",
      sub: !p ? " " : `${n(p.open.count, "Rechnung", "Rechnungen")}${p.open.nextDue ? ` · nächste fällig ${short(p.open.nextDue)}` : ""}`,
    },
    {
      id: "paid", label: "Bezahlt", dot: "bg-muted-foreground/50",
      amount: p ? centsEur(p.paid.cents) : "–", sub: p ? `seit ${short(p.paid.since)}` : " ",
    },
  ];
}

/** The four steps from effort to payment; each is a toggle that filters the page below. */
export function Pipeline({ data, stage, onPick }: { data: PipelineData | undefined; stage: Stage | null; onPick: (s: Stage | null) => void }) {
  return (
    <section aria-label="Vom Aufwand zum Zahlungseingang" className="overflow-hidden rounded-lg border bg-card">
      <div className="grid grid-cols-1 min-[520px]:grid-cols-2 lg:grid-cols-4 [&>*:not(:first-child)]:border-t min-[520px]:[&>*:nth-child(2)]:border-t-0 lg:[&>*:not(:first-child)]:border-t-0 lg:[&>*:not(:first-child)]:border-l min-[520px]:max-lg:[&>*:nth-child(even)]:border-l">
        {stages(data).map((s) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={stage === s.id}
            onClick={() => onPick(stage === s.id ? null : s.id)}
            className={cn(
              "relative flex min-w-0 flex-col items-start gap-0.5 px-4 py-3.5 text-left outline-none transition-colors duration-150 hover:bg-muted focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:ring-inset motion-reduce:transition-none pointer-coarse:min-h-11",
              stage === s.id && "bg-muted after:absolute after:inset-x-4 after:bottom-0 after:h-0.5 after:rounded after:bg-foreground",
            )}
          >
            <span className="flex items-center gap-2 text-[0.8125rem] text-muted-foreground"><span aria-hidden className={cn("size-1.5 shrink-0 rounded-full", s.dot)} />{s.label}</span>
            <span className="text-xl font-semibold tracking-tight tabular-nums">{s.amount}</span>
            <span className="max-w-full truncate text-[0.8125rem] text-muted-foreground">{s.sub}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
